/**
 * 管理后台「模型与供应商」五页的接口与类型：供应商配置、渠道模型库、已发布模型、AI 自动选模、通用能力。
 *
 * 背景：这五页对应 Alice 的 /providers*、/upstream-cost-prices、/models*、/auto-route-policy*、/decisions-shadow/*、
 *   /stats/capabilities*、/vendor-price-refs*、/exchange-rate；后端都在 gateway_control（/control/admin/...）。
 * 设计意图：
 *   - 不改共用的 admin.ts：原有的模型、渠道、路由、渠道模型库、降级、告警接口直接沿用 adminApi 的实现并在
 *     catalogApi 里转出（页面只需引一个模块），本文件新增的接口自己发请求，路径逐字对应后端。
 *   - 遇到 401 先调 adminApi.me() 触发共用 http 层的会话刷新（同一时刻只刷新一次，避免并发轮换刷新令牌），
 *     成功后重试一次；失败时 me() 会通知应用回登录页。与 admin-runtime2.ts 同一做法。
 * 关键约束：金额字段 *_units 是整数 unit（1e-8 美元），price_* 与 list_* 是原币种的十进制字符串；
 *   null 表示成本或价格未知，不当作 0；错误统一抛 ApiError。
 */
import { adminApi } from './admin.ts'
import { ApiError, type Query } from './http.ts'
import type { Channel, ChannelModel, ChannelOptions, FallbackEvent, FallbackStat, Model, ModelKind, ProbeResult, ProviderType, Route } from './types.ts'

// ------------------------------------------------------------------ 类型

/** 在共用的 Channel 上多出 Logo 地址。 */
export interface CatalogChannel extends Channel {
  icon_url: string
}

export type PresetCategory = 'global' | 'china' | 'coding' | 'platform'

/** 预置供应商目录的一条（后端 channel_presets.PRESETS）；installed 是已用同一 ID 或同一地址建好的渠道。 */
export interface ChannelPreset {
  id: string
  name: string
  category: PresetCategory
  provider_type: ProviderType
  base_url: string
  docs_url: string
  icon_url: string
  options: ChannelOptions
  installed: string[]
}

export interface PresetInstallInput {
  api_key: string
  channel_id?: string
  name?: string
  base_url?: string
}

/** 安装结果：渠道本身，加上探测耗时与模型登记情况。models_endpoint=false 表示上游没有模型清单接口。 */
export interface PresetInstallResult extends CatalogChannel {
  config_version: number
  latency_ms: number
  models_endpoint: boolean
  models_registered: number
  models_by_kind: Record<string, number>
  models_priced: number
}

/** 在共用的 Model 上多出图标、标签与白名单账号（public=false 时只对白名单账号列出）。 */
export interface CatalogModel extends Model {
  icon_url: string
  tags: string[]
  allowed_accounts: string[]
}

export type ChannelModelSource = 'manual' | 'fetched' | 'published' | 'migrated'

/** 渠道模型库一行：共用字段 + 渠道名、近 N 天调用与成本未知数、路由引用数、是否缺采购价、来源。 */
export interface LibraryItem extends ChannelModel {
  source: ChannelModelSource
  channel_name: string
  requests: number
  unknown_cost_requests: number
  route_refs: number
  cost_missing: boolean
}

export interface ChannelStat {
  requests: number
  ok: number
  p95_ms: number | null
  fallback_switches: number
  routes_active: number
  routes_total: number
  models: number
}

export interface FetchedModel {
  model_id: string
  context_length: number | null
  registered: boolean
}

export interface AlertConfig {
  email_configured: boolean
  email_masked: string
  webhook_configured: boolean
  all_failed_threshold: number
  window_minutes: number
}

export type MarginLevel = 'loss' | 'zero' | 'ok' | 'unknown' | 'skipped' | 'na'

export interface MarginRow {
  model_id: string
  kind: ModelKind
  level: MarginLevel
  missing_cost_routes: string[]
  sell_usd?: Record<string, string>
  cost_usd?: Record<string, string>
  markup?: Record<string, string | null>
}

export interface PublishItem {
  channel_id: string
  upstream_model: string
  model_id?: string
  display_name?: string
  kind?: ModelKind
  markup?: string
}

export interface PublishResult {
  channel_id: string
  upstream_model: string
  model_id: string
  status: 'created' | 'exists' | 'error'
  error?: string
  kind?: ModelKind
  enabled?: boolean
  needs_pricing?: boolean
}

export interface RepriceRow {
  model_id: string
  status: 'ready' | 'skipped' | 'applied'
  reason?: string
  cost_usd?: Record<string, string>
  after?: Record<string, string>
}

export interface VendorRef {
  source: string
  ref_id: string
  display_name: string
  currency: string
  prompt: string | null
  completion: string | null
  cache_read: string | null
  cache_write: string | null
  per_image: string | null
  context_length: number | null
  fetched_at: string
}

export interface VendorSource {
  source: string
  models: number
  fetched_at: string | null
  last_error: string
}

export type RouteLevel = 'economy' | 'quality'

export interface AutoRule {
  id: string
  enabled?: boolean
  when: Record<string, unknown>
  route: RouteLevel
  model_id?: string
}

export interface AutoScene {
  mode: 'shadow' | 'enforce'
  strategy: 'fixed' | 'rules'
  fallback_model_id: string
  economy_model_id: string
  quality_model_id: string
  default_route?: RouteLevel
  rules?: AutoRule[]
  shadow_rules?: AutoRule[]
  shadow_default_route?: RouteLevel
  binding_ttl_seconds?: number
}

export interface AutoPolicy {
  schema_version: number
  binding_epoch: number
  auto_model_id?: string
  default_scene_id: string
  scenes: Record<string, AutoScene>
  policy_version?: string
}

export interface ShadowConfig {
  enabled: boolean
  sample_rate: number
}

export interface AutoPolicyDoc {
  source: 'default' | 'saved'
  policy: AutoPolicy
  policy_version: string
  shadow_config: ShadowConfig
  updated_by: string
  updated_at: string | null
  chat_models: string[]
}

export interface AutoDecision {
  scene_id: string
  sample: string
  level: RouteLevel
  model_id: string
  reason: string
  mode: string
  actual_model: string
}

export interface AutoPreview {
  ok: boolean
  error: string
  policy?: AutoPolicy
  unroutable_models?: string[]
  decisions?: AutoDecision[]
}

export interface AutoStatRow {
  scene_id: string
  mode: string
  reason: string
  resolved_model: string
  requests: number
  avg_decision_us: number
}

export interface ShadowRow {
  scene_id: string
  events: number
  success_rate: number | null
  agreement_rate: number | null
  primary_p50_ms: number | null
  primary_p95_ms: number | null
  shadow_p50_ms: number | null
  shadow_p95_ms: number | null
  cross: Record<string, number>
}

export interface Capability {
  model_id: string
  label: string
  kind: ModelKind
  enabled: boolean
  price_currency: 'USD' | 'CNY'
  price_unit: 'per_image' | 'per_million_tokens' | 'per_million_chars' | 'per_million_seconds' | 'per_minute' | null
  price: string | null
  price_editable: boolean
  requests: number
  accounts: number
  cost: number
  upstream_cost: number
  unknown_cost_requests: number
  failures: number
  last_used_at: string | null
}

export interface CapabilityRecent {
  request_id: string
  created_at: string
  account_id: string
  model_id: string
  label: string
  kind: ModelKind
  channel_id: string
  upstream_model: string
  duration_ms: number
  cost: number
  upstream_cost: number | null
}

export interface CapabilityStats {
  summary: { requests: number; accounts: number; cost: number; today_requests: number; failures: number }
  capabilities: Capability[]
  total: number
  recent: CapabilityRecent[]
}

export type ModelPatch = Partial<{
  display_name: string
  description: string
  public: boolean
  enabled: boolean
  sort: number
  icon_url: string
  tags: string[]
  allowed_accounts: string[]
  thinking_policy: Model['thinking_policy']
  default_max_tokens: number
  max_input_chars: number
}>

export type ChannelPatch = Partial<{
  name: string
  provider_type: Channel['provider_type']
  base_url: string
  api_key: string
  icon_url: string
  max_concurrent: number
  rpm: number
  enabled: boolean
  options: Channel['options']
  auto_disable: boolean
  failure_threshold: number
  cooldown_seconds: number
}>

export type LibraryPatch = Partial<{
  note: string
  enabled: boolean
  price_currency: 'USD' | 'CNY'
  list_prompt: string | null
  list_completion: string | null
  list_cache_read: string | null
  list_cache_write: string | null
  list_per_image: string | null
  cost_discount: string | null
  context_length: number | null
}>

// ------------------------------------------------------------------ 请求层

function failure(status: number, body: unknown): ApiError {
  const err = (body as { error?: { code?: string; message?: string } } | null)?.error
  return new ApiError(status, err?.code ?? `http_${status}`, err?.message ?? 'Request failed')
}

function url(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === 'all') continue
    qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

async function request<T>(method: string, path: string, query?: Query, body?: unknown): Promise<T> {
  const target = url(path, query)
  for (let attempt = 0; attempt < 2; attempt++) {
    let resp: Response
    try {
      resp = await fetch(target, {
        method,
        credentials: 'same-origin',
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (e) {
      console.error('[api] 网络错误', method, target, e)
      throw new ApiError(0, 'network_error', 'Network error')
    }
    const text = await resp.text()
    let parsed: unknown
    try {
      parsed = text ? JSON.parse(text) : undefined
    } catch {
      parsed = undefined
    }
    if (resp.status === 401 && attempt === 0) {
      try {
        await adminApi.me()
      } catch (e) {
        console.warn('[api] 会话刷新失败，不再重试', e)
        throw failure(resp.status, parsed)
      }
      continue
    }
    if (!resp.ok) {
      const err = failure(resp.status, parsed)
      console.warn('[api] 请求失败', method, target, err.status, err.code)
      throw err
    }
    return parsed as T
  }
  throw new ApiError(401, 'unauthorized', 'Unauthorized')
}

const C = '/control/admin'
const enc = encodeURIComponent

// ------------------------------------------------------------------ 接口

export const catalogApi = {
  // 沿用 admin.ts 的实现（类型按本文件扩展后的形状收窄）
  models: () => adminApi.models() as Promise<{ data: CatalogModel[] }>,
  putModel: adminApi.putModel,
  putRoutes: adminApi.putRoutes,
  channels: () => adminApi.channels() as Promise<{ data: CatalogChannel[] }>,
  putChannel: adminApi.putChannel,
  probeChannel: (id: string): Promise<ProbeResult> => adminApi.probeChannel(id),
  resetChannelHealth: adminApi.resetChannelHealth,
  channelModels: adminApi.channelModels,
  putChannelModel: adminApi.putChannelModel,
  deleteChannelModel: adminApi.deleteChannelModel,
  fallbackEvents: (filter: { model?: string; kind?: string; limit?: number } = {}): Promise<{ data: FallbackEvent[] }> =>
    adminApi.fallbackEvents(filter),
  fallbackStats: (minutes = 30): Promise<{ minutes: number; data: FallbackStat[] }> => adminApi.fallbackStats(minutes),

  // 供应商配置
  channelsOverview: (hours = 24) => request<{ hours: number; data: Record<string, ChannelStat> }>('GET', `${C}/channels/overview`, { hours }),
  patchChannel: (id: string, body: ChannelPatch) => request<CatalogChannel & { config_version: number }>('PATCH', `${C}/channels/${enc(id)}`, undefined, body),
  testChannel: (id: string) => request<{ ok: boolean; status?: number; error?: string; latency_ms: number; models: number | null }>('POST', `${C}/channels/${enc(id)}/test`),
  fetchChannelModels: (id: string) => request<{ latency_ms: number; models: FetchedModel[] }>('POST', `${C}/channels/${enc(id)}/fetch-models`),
  deleteChannel: (id: string) => request<{ deleted: boolean; config_version: number }>('DELETE', `${C}/channels/${enc(id)}`),
  channelPresets: () => request<{ categories: PresetCategory[]; items: ChannelPreset[] }>('GET', `${C}/channel-presets`),
  installPreset: (presetId: string, body: PresetInstallInput) =>
    request<PresetInstallResult>('POST', `${C}/channel-presets/${enc(presetId)}/install`, undefined, body),
  alertConfig: () => request<AlertConfig>('GET', `${C}/alert-config`),

  // 已发布模型
  patchModel: (id: string, body: ModelPatch) => request<CatalogModel & { config_version: number }>('PATCH', `${C}/models/${enc(id)}`, undefined, body),
  deleteModel: (id: string) => request<{ deleted: boolean; config_version: number }>('DELETE', `${C}/models/${enc(id)}`),
  copyModel: (id: string, new_id: string, display_name = '') =>
    request<CatalogModel & { config_version: number }>('POST', `${C}/models/${enc(id)}/copy`, undefined, { new_id, display_name }),
  publishModels: (items: PublishItem[]) =>
    request<{ results: PublishResult[]; created: number; config_version: number | null }>('POST', `${C}/models/publish`, undefined, { items }),
  margins: () => request<{ cny_usd_rate: number; data: MarginRow[] }>('GET', `${C}/models/margins`),
  reprice: (model_ids: string[] | null, markup: string, dry_run: boolean) =>
    request<{ dry_run: boolean; plan: RepriceRow[]; applied?: number; config_version: number | null }>('POST', `${C}/models/reprice`, undefined, { model_ids, markup, dry_run }),
  exchangeRate: () => request<{ cny_per_usd: number; source: string }>('GET', `${C}/exchange-rate`),

  // 渠道模型库
  libraryOverview: (days = 30, channel_id?: string) => request<{ days: number; data: LibraryItem[] }>('GET', `${C}/channel-models/overview`, { days, channel_id }),
  patchLibraryItem: (channel_id: string, upstream_model: string, body: LibraryPatch) =>
    request<ChannelModel & { source: ChannelModelSource; config_version: number }>('PATCH', `${C}/channels/${enc(channel_id)}/models/${upstream_model.split('/').map(enc).join('/')}`, undefined, body),
  vendorRefs: (model_id = '', limit = 12) => request<{ sources: VendorSource[]; items: VendorRef[] }>('GET', `${C}/vendor-price-refs`, { model_id, limit }),
  refreshVendorRefs: () => request<{ source: string; items: number }>('POST', `${C}/vendor-price-refs/refresh`),

  // AI 自动选模
  autoPolicy: () => request<AutoPolicyDoc>('GET', `${C}/auto-route-policy`),
  previewAutoPolicy: (policy: unknown, samples?: string[]) => request<AutoPreview>('POST', `${C}/auto-route-policy/preview`, undefined, { policy, samples }),
  putAutoPolicy: (policy: unknown) => request<{ policy: AutoPolicy; policy_version: string; config_version: number }>('PUT', `${C}/auto-route-policy`, undefined, { policy }),
  autoStats: (hours = 24) => request<{ hours: number; total: number; data: AutoStatRow[] }>('GET', `${C}/auto-route-stats`, { hours }),
  shadowSummary: (hours = 24) => request<{ hours: number; data: ShadowRow[] }>('GET', `${C}/decisions-shadow/summary`, { hours }),
  shadowConfig: () => request<ShadowConfig & { policy_saved: boolean }>('GET', `${C}/decisions-shadow/config`),
  putShadowConfig: (cfg: ShadowConfig) => request<ShadowConfig & { policy_saved: boolean; config_version: number }>('PUT', `${C}/decisions-shadow/config`, undefined, cfg),

  // 通用能力
  capabilities: (q: { start?: string; end?: string; limit?: number; offset?: number } = {}) => request<CapabilityStats>('GET', `${C}/stats/capabilities`, q as Query),
  putCapabilityPrice: (model_id: string, price: string) =>
    request<{ model_id: string; price: string; config_version: number }>('PUT', `${C}/stats/capabilities/${enc(model_id)}/price`, undefined, { price }),
}

export type { Route }
